import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { isMeghaPublicProfileUrlPath } from './megha/public-digital-profile/megha-public-profile-path.util';
import { isAdminShellRoutePath } from './shared/constants/admin-shell-route.util';
import { IndexShellChromeService } from './shared/services/index-shell-chrome.service';
import { SupabaseService } from './shared/services/supabase.service';
import { TekMeghaFcmTokenService } from './shared/services/tekmegha-fcm-token.service';
import {
  isSupabaseUserLoggedIn,
  setMeghaChatHiddenForLoggedInUser
} from './shared/utils/megha-chat-auth.util';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  template: '<router-outlet></router-outlet>',
  styles: [
    `
      :host {
        display: block;
        box-sizing: border-box;
      }
    `
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'tekmegha-home';
  private routerSubscription?: Subscription;
  private adminShellChromeHidden = false;
  private authUnsubscribe?: () => void;

  constructor(
    private router: Router,
    private indexShellChrome: IndexShellChromeService,
    private supabase: SupabaseService,
    private tekmeghaFcm: TekMeghaFcmTokenService
  ) {}

  /** Domains where chat is HIDDEN (Angular app). Chat allowed on www.tekmegha.com only for non-Angular routes; other domains always allow chat. */
  private readonly CHAT_HIDDEN_HOSTS = ['tekmegha.com', 'www.tekmegha.com'];

  ngOnInit() {
    // Hide megha live chat on tekmegha.com Angular routes only; show on other domains
    this.applyChatVisibilityByDomain();
    this.applyLoggedInChatHide();
    const { data } = this.supabase.onAuthStateChange((event, session) => {
      // Prefer live session from Supabase over localStorage parse (reliable right after login)
      setMeghaChatHiddenForLoggedInUser(!!session?.user || isSupabaseUserLoggedIn());
      this.applyIndexShellChrome();
      this.syncFcmTokenOnAuth(event, session);
    });
    this.authUnsubscribe = () => data.subscription.unsubscribe();
    // Widget may mount after login — re-apply hide when it becomes ready
    window.addEventListener('meghaChatReady', this.onMeghaChatReady);
    window.addEventListener('tekmegha-app-detected', this.onTekMeghaAppDetected);
    this.applyPublicProfileChatHide();
    this.applyIndexShellChrome();

    // Handle password recovery redirect from Supabase
    // Supabase redirects to the base URL with recovery token in hash fragment
    this.handlePasswordRecoveryRedirect();
    
    // Scroll to top on route change; sync index.html footer chrome with Angular routes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        window.scrollTo(0, 0);
        this.applyLoggedInChatHide();
        this.applyPublicProfileChatHide();
        this.applyIndexShellChrome();
      });
  }

  private readonly onMeghaChatReady = (): void => {
    this.applyLoggedInChatHide();
  };

  /** Android injects token after page load — retry upsert if already signed in. */
  private readonly onTekMeghaAppDetected = (): void => {
    void this.tekmeghaFcm.syncTokenForCurrentUser();
  };

  private syncFcmTokenOnAuth(event: string, session: { user?: unknown } | null): void {
    if (event === 'SIGNED_OUT') {
      this.tekmeghaFcm.clearLocalSyncState();
      return;
    }
    if (!session?.user) return;
    if (
      event === 'SIGNED_IN' ||
      event === 'INITIAL_SESSION' ||
      event === 'TOKEN_REFRESHED' ||
      event === 'USER_UPDATED'
    ) {
      void this.tekmeghaFcm.syncTokenForCurrentUser();
    }
  }

  /** Do not show public enquiry chat widget to logged-in store owners / admins. */
  private applyLoggedInChatHide(): void {
    setMeghaChatHiddenForLoggedInUser(isSupabaseUserLoggedIn());
  }

  /**
   * Hide index.html site footer for signed-in users and back-office routes
   * where an app shell provides navigation.
   */
  private applyIndexShellChrome(): void {
    const hide = isAdminShellRoutePath(window.location.pathname) || isSupabaseUserLoggedIn();
    if (hide && !this.adminShellChromeHidden) {
      this.indexShellChrome.hideIndexShellChrome();
      this.adminShellChromeHidden = true;
    } else if (!hide && this.adminShellChromeHidden) {
      this.indexShellChrome.showIndexShellChrome();
      this.adminShellChromeHidden = false;
    }
  }

  private applyPublicProfileChatHide() {
    const onPublicProfile = isMeghaPublicProfileUrlPath(window.location.pathname);
    document.body.classList.toggle('megha-hide-chat-on-profile', onPublicProfile);
  }

  private applyChatVisibilityByDomain() {
    const host = window.location.hostname.toLowerCase();
    const isTekmeghaDomain = this.CHAT_HIDDEN_HOSTS.some(h => host === h);
    // Hide chat on tekmegha.com (Angular routes); allow on other domains (dentalcastle.in, store.tekmegha.com, etc.)
    if (isTekmeghaDomain) {
      document.body.classList.add('hide-megha-chat');
    } else {
      document.body.classList.remove('hide-megha-chat');
    }
  }

  private handlePasswordRecoveryRedirect() {
    // Check if we're on the root path with a password recovery hash fragment
    // Supabase redirects to the base URL with recovery token in hash fragment
    const isRootPath = window.location.pathname === '/' || window.location.pathname === '';
    const urlHash = window.location.hash;
    
    if (isRootPath && urlHash) {
      // Check if this contains password recovery tokens
      if (urlHash.includes('type=recovery') || urlHash.includes('access_token')) {
        // Redirect to reset password confirm page, preserving the hash fragment
        // Angular router will preserve the hash automatically
        this.router.navigate(['/reset-password-confirm'], { 
          fragment: urlHash.substring(1), // Remove the '#' prefix
          replaceUrl: true // Replace in history so back button doesn't go to root
        });
      }
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.authUnsubscribe?.();
    window.removeEventListener('meghaChatReady', this.onMeghaChatReady);
    window.removeEventListener('tekmegha-app-detected', this.onTekMeghaAppDetected);
  }
}

